import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import ConnString from "../ConnString";

const API = ConnString.connstring;
const quantity = (value) => Number(value || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const useItemInventoryPositions = (itemCodes, warehouseCode, onError) => {
  const itemCodeKey = useMemo(() => [...new Set((itemCodes || []).map((value) => String(value || "").trim().toUpperCase()).filter(Boolean))].join("|"), [itemCodes]);
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const codes = itemCodeKey ? itemCodeKey.split("|") : [];
    if (!codes.length) { setPositions({}); setLoading(false); return undefined; }
    const controller = new AbortController();
    setLoading(true);
    fetch(`${API}/api/inventory/purchase-orders/item-inventory-position`, {
      method: "POST", credentials: "include", signal: controller.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ compID: sessionStorage.getItem("compID"), itemCodes: codes, warehouseCode: warehouseCode || "" }),
    }).then(async (response) => {
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Unable to load item inventory positions.");
      return data;
    }).then((data) => {
      const grouped = (data.positions || []).reduce((result, row) => {
        const key = String(row.ItemCode || "").trim().toUpperCase();
        if (!result[key]) result[key] = [];
        result[key].push(row);
        return result;
      }, {});
      setPositions(grouped);
    }).catch((error) => {
      if (error.name !== "AbortError" && onError) onError(error.message || "Unable to load item inventory positions.");
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [itemCodeKey, warehouseCode, onError]);

  return { positions, loading };
};

const TooltipContent = ({ rows, loading }) => <Box sx={{ minWidth: 360, p: 0.5 }}>
  <Typography fontWeight={800} mb={0.75}>Item Inventory Position</Typography>
  {loading ? <Stack direction="row" alignItems="center" gap={1}><CircularProgress size={15} color="inherit" /><span>Loading inventory…</span></Stack> : rows.length ? <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "th,td": { px: 0.75, py: 0.4, whiteSpace: "nowrap" }, th: { borderBottom: "1px solid rgba(255,255,255,.35)", textAlign: "right" }, "th:first-of-type,td:first-of-type": { textAlign: "left" }, td: { textAlign: "right" } }}><thead><tr><th>Warehouse</th><th>Unit</th><th>On Order</th><th>On Hand</th><th>Allocated</th><th>Available</th></tr></thead><tbody>{rows.map((row) => <tr key={`${row.ItemCode}-${row.Warehouse}`}><td>{row.Warehouse || "—"}</td><td>{row.Unit || "—"}</td><td>{quantity(row.OnOrderQty)}</td><td>{quantity(row.OnHandQty)}</td><td>{quantity(row.AllocatedQty)}</td><td><b>{quantity(row.AvailQty)}</b></td></tr>)}</tbody></Box> : <Typography variant="body2">No inventory position found for this item and warehouse.</Typography>}
</Box>;

const InventoryPositionTooltip = ({ itemCode, positions = {}, loading = false, children }) => {
  const key = String(itemCode || "").trim().toUpperCase();
  return <Tooltip arrow placement="top-start" enterDelay={250} slotProps={{ tooltip: { sx: { maxWidth: "none" } } }} title={<TooltipContent rows={positions[key] || []} loading={loading} />}><Box component="span" tabIndex={0} sx={{ cursor: "help", borderBottom: "1px dotted", borderColor: "text.secondary" }}>{children ?? itemCode}</Box></Tooltip>;
};

export default InventoryPositionTooltip;
